import React, { FC, useEffect, useState } from 'react';
import { TableColumn } from '@/models/table/TableColumn';
import { TableRowGroup } from '../../../models/table/TableRowGroup';
import TableRow from '../table-row/table-row';
import EmptyTableState from '@/components/emptyTableState/EmptyTableState';
import { useTable } from '@/contexts/table/TableContext';

type Props = {
    columnDefinitions: TableColumn[],
    childTableHeading?: string,
    highlightExpandedParent?: boolean,
    rows: any[],
    groupedRows?: TableRowGroup[],
    selectRows: boolean,
    onSelect: (index: number) => void,
    onRowClicked?: (rowData: any) => void,
    customHighlightRow?: any
    tableId?: string
    customRowColourWithRange?: any
    rowPath?: string,
    isStriped?: boolean
}

const TableBody: FC<Props> = ({ columnDefinitions = [], childTableHeading, highlightExpandedParent, rows = [], groupedRows = [], selectRows = false, onSelect = () => { }, onRowClicked, customHighlightRow, tableId,  customRowColourWithRange, rowPath, isStriped = true}) => {
    const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
    const tableContext = useTable();

    useEffect(() => {
        if (groupedRows.length === 0) {
            return;
        }

        setCollapsedGroups((currentState) => {
            const nextState: Record<string, boolean> = {};
            groupedRows.forEach((group) => {
                const groupId = `${group.id}`;
                nextState[groupId] = currentState[groupId] ?? group.initiallyCollapsed ?? false;
            });
            return nextState;
        });
    }, [groupedRows]);

    const checkHiglightRow = (item) => {
        if (customHighlightRow) {
            return (item.data[customHighlightRow?.fieldName] === customHighlightRow?.comparisonValue ? true : false)
        }
        return false;
    }

    const checkCustomRowColour = (item) => {
        if (customRowColourWithRange) {
            return (item.data[customRowColourWithRange?.fieldName] === customRowColourWithRange?.comparisonValue ? true : false)

        }
        return false;
    }

    const toggleGroup = (groupId: string | number) => {
        setCollapsedGroups((currentState) => {
            const nextState = {
                ...currentState,
                [`${groupId}`]: !currentState[`${groupId}`]
            };
            tableContext.syncCollapsedGroups?.(nextState);
            return nextState;
        });
    }

    if (groupedRows.length > 0) {
        let flatRowIndex = 0;
        let visibleRowIndex = 0;

        return <>
            <tbody>
                {groupedRows.map((group) => {
                    const groupId = `${group.id}`;
                    const isCollapsed = collapsedGroups[groupId] ?? false;
                    const parentRowIndex = flatRowIndex;
                    const parentVisibleRowIndex = visibleRowIndex;
                    flatRowIndex += 1;
                    visibleRowIndex += 1;

                    return <React.Fragment key={`group-${groupId}`}>
                        <TableRow
                            columnDefinitions={columnDefinitions}
                            key={`group-parent-${groupId}`}
                            rowData={group.parentRow}
                            isSelected={selectRows ? rows[parentRowIndex]?.selected : undefined}
                            onRowClicked={onRowClicked}
                            isRowHighlighted={highlightExpandedParent === true && !isCollapsed}
                            onSelect={() => onSelect(parentRowIndex)}
                            tableId={tableId}
                            customRowColourWithRange={customRowColourWithRange}
                            showCustomRowColour={false}
                            rowPath={rowPath}
                            isStriped={isStriped && parentVisibleRowIndex % 2 !== 0}
                            rowIndex={parentRowIndex}
                            testId={parentRowIndex}
                            groupRow={{
                                kind: 'parent',
                                isExpanded: !isCollapsed,
                                childCount: group.childCount ?? group.childRows.length,
                                onToggle: () => toggleGroup(group.id),
                                childRows: group.childRows
                            }}
                        />
                        {!isCollapsed && (tableContext.childColumns
                            ? (
                                <tr key={`group-children-${groupId}`}>
                                    <td
                                        colSpan={columnDefinitions.filter(c => c.active).length + (selectRows ? 1 : 0)}
                                        className="p-0 pb-2"
                                    >
                                        <div className="pt-3">
                                        {childTableHeading && (
                                            <div className="pb-1 text-sm font-semibold text-gray-600" style={{ marginLeft: '1.5rem' }}>
                                                {childTableHeading}
                                            </div>
                                        )}
                                        <div style={{ marginLeft: '1.5rem', borderLeft: '3px solid #d0d2d6' }}>
                                            <table className="w-full">
                                                <thead>
                                                    <tr>
                                                            {tableContext.childColumns.filter(c => c.active).map((col, colIdx) => (
                                                            <th
                                                                key={colIdx}
                                                                className="px-3 py-1 bg-gray-300 text-xs text-left font-normal border-x border-b whitespace-nowrap"
                                                            >
                                                                {col.heading}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {group.childRows.map((childRow, childIdx) => (
                                                        <TableRow
                                                            key={`group-child-${groupId}-${childRow.id ?? childIdx}`}
                                                            columnDefinitions={tableContext.childColumns ?? tableContext.tableDescriptor}
                                                            rowData={childRow}
                                                            isSelected={undefined}
                                                            onRowClicked={onRowClicked}
                                                            isRowHighlighted={false}
                                                            onSelect={() => {}}
                                                            isStriped={isStriped && childIdx % 2 !== 0}
                                                            rowIndex={childIdx}
                                                            testId={childIdx}
                                                        />
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        </div>
                                    </td>
                                </tr>
                            )
                            : group.childRows.map((childRow) => {
                                const childRowIndex = flatRowIndex;
                                const childVisibleRowIndex = visibleRowIndex;
                                flatRowIndex += 1;
                                visibleRowIndex += 1;

                                return <TableRow
                                    columnDefinitions={columnDefinitions}
                                    key={`group-child-${groupId}-${childRow.id}`}
                                    rowData={childRow}
                                    isSelected={selectRows ? rows[childRowIndex]?.selected : undefined}
                                    onRowClicked={onRowClicked}
                                    isRowHighlighted={false}
                                    onSelect={() => onSelect(childRowIndex)}
                                    tableId={tableId}
                                    customRowColourWithRange={customRowColourWithRange}
                                    showCustomRowColour={false}
                                    rowPath={rowPath}
                                    isStriped={isStriped && childVisibleRowIndex % 2 !== 0}
                                    rowIndex={childRowIndex}
                                    testId={childRowIndex}
                                    groupRow={{ kind: 'child' }}
                                />
                            })
                        )}
                    </React.Fragment>;
                })}
            </tbody>
        </>;
    }

    return <tbody>
        {
            rows.length === 0 ?
                <tr className='items-center'>
                    {/* this is to ensure the empty state takes up the full width of the table. Is selectRows is true an extra column is added */}
                    <td colSpan={columnDefinitions.map(col => col.active).length + (selectRows ? 1 : 0)}>
                        <EmptyTableState
                            message={tableContext.emptyStateMessage}
                            onAddNew={tableContext.onAddNew}
                            showAddNewButton={tableContext.showAddNewButton}
                            rowPath={rowPath} />
                    </td>
                </tr> :
                rows.map((item, index) => (
                    <TableRow
                        columnDefinitions={columnDefinitions}
                        key={`row-${index}`}
                        rowData={item.data}
                        isSelected={selectRows ? item.selected : undefined}
                        onRowClicked={onRowClicked}
                        isRowHighlighted={checkHiglightRow(item)}
                        onSelect={() => onSelect(index)}
                        tableId={tableId}
                        customRowColourWithRange={customRowColourWithRange}
                        showCustomRowColour={checkCustomRowColour(item)}
                        rowPath={rowPath}
                        isStriped={isStriped && index % 2 != 0}
                        rowIndex={index}
                        testId={index}
                    />
                ))
        }
    </tbody>
}

export default TableBody;